"use client";

import React from "react";
import { Icon } from "@ama-pt/agora-design-system";

type Booleanish = boolean | "true" | "false";

interface AgoraButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  appearance?: "solid" | "outline" | "link";
  variant?: "primary" | "neutral" | "success" | "warning" | "danger" | "informative";
  fullWidth?: Booleanish;
  hasIcon?: Booleanish;
  iconOnly?: Booleanish;
  leadingIcon?: string;
  trailingIcon?: string;
}

const isEnabled = (value?: Booleanish) => value === true || value === "true";

const ButtonIcon = ({
  name,
  position,
}: {
  name?: string;
  position: "leading" | "trailing";
}) => {
  if (!name) return null;

  return (
    <span className={`icon-wrapper ${position}`} aria-hidden="true">
      <Icon className={`${position}-icon-default`} name={name} />
    </span>
  );
};

const AgoraButton = React.forwardRef<HTMLButtonElement, AgoraButtonProps>(
  (
    {
      appearance = "solid",
      variant = "primary",
      fullWidth,
      hasIcon,
      iconOnly,
      leadingIcon,
      trailingIcon,
      className = "",
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const showIcon = isEnabled(hasIcon);
    const hideChildren = isEnabled(iconOnly);

    const classes = [
      "agora-btn",
      `agora-btn-${appearance}-${variant}`,
      isEnabled(fullWidth) ? "agora-btn-full" : "",
      showIcon ? "agora-btn-with-icon" : "",
      hideChildren ? "agora-btn-icon-only" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button ref={ref} type={type} className={classes} {...props}>
        {showIcon && (
          <ButtonIcon
            position="leading"
            name={leadingIcon}
          />
        )}
        {!hideChildren && children && (
          <span className="children-wrapper">{children}</span>
        )}
        {showIcon && (
          <ButtonIcon
            position="trailing"
            name={trailingIcon}
          />
        )}
      </button>
    );
  }
);

AgoraButton.displayName = "AgoraButton";

export default AgoraButton;
